// Pathway Pages Mock Data

export const after10thData = {
  hero: {
    breadcrumb: ['Home', 'Pathways', 'After 10th / Inter'],
    title: 'After 10th / Inter: Explore & Decide',
    subtitle: '4 short tests to find the stream that fits you.',
    primaryCTA: { text: 'Start 4 Tests', link: '#tests' },
    heroImage: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45'
  },
  stepper: ['4 Tests', 'Results', 'Samplers', 'Reality Check', 'Decision Report'],
  whatYouGet: [
    {
      icon: 'Target',
      title: 'Results Summary + Top 3 Matches',
      description: 'See which streams match your interests, aptitude, and work style.'
    },
    {
      icon: 'Lightbulb',
      title: 'Why This Match',
      description: 'Understand the reasoning behind each recommendation.'
    },
    {
      icon: 'Map',
      title: 'Next-Step Roadmap Preview',
      description: 'Get a glimpse of what you will learn in each stream.'
    }
  ],
  samplers: [
    {
      lang: 'Python',
      topics: ['Variables and Data Types', 'Loops and Conditions', 'Functions', 'Lists and Dictionaries', 'Simple Projects'],
      link: '/pages/python-free-sampler'
    },
    {
      lang: 'Java',
      topics: ['Classes and Objects', 'Basic Syntax', 'If-Else and Loops', 'Arrays', 'Simple Calculator'],
      link: '/pages/java-free-sampler'
    },
    {
      lang: 'C',
      topics: ['Printf and Scanf', 'Variables', 'For and While Loops', 'Arrays', 'Simple Programs'],
      link: '/samplers/c'
    }
  ],
  realityCheck: [
    { title: 'What You Do', text: 'Write code, solve problems, debug errors, build projects.' },
    { title: 'Tools Used', text: 'VS Code, Git, Terminal, Libraries, IDEs.' },
    { title: 'Hard Parts', text: 'Logic building, debugging, staying updated with tech.' },
    { title: 'Who Should Avoid', text: 'If you hate problem-solving or sitting for long hours.' }
  ],
  compareOptions: {
    title: 'Compare Streams',
    options: [
      { stream: 'CSE (Computer Science)', workType: 'Coding, Software', skills: 'Logic, Problem-solving', difficulty: 'High', aiRisk: 'Medium' },
      { stream: 'ECE (Electronics)', workType: 'Circuits, Embedded', skills: 'Math, Hardware', difficulty: 'Very High', aiRisk: 'Low' },
      { stream: 'Pharmacy/Biotech', workType: 'Research, Labs', skills: 'Biology, Chemistry', difficulty: 'Medium', aiRisk: 'Low' }
    ]
  },
  faq: [
    { q: 'Can I change my stream later?', a: 'Yes, but early clarity saves time and effort.' },
    { q: 'Do I need to decide now?', a: 'The sooner you explore, the better prepared you will be.' },
    { q: 'Are these 4 tests accurate?', a: 'They are a guide based on your interests, work style, strengths, and skill readiness — not a verdict.' }
  ],
  parentNote: 'Parents: This tool helps reduce confusion and guides students toward informed decisions based on fit, not just marks.'
};

export const firstYearData = {
  hero: {
    breadcrumb: ['Home', 'Pathways', 'Choose Specialization'],
    title: 'B.Tech / BBA / MBA: Choose Specialization',
    subtitle: 'Specialization -> role path -> 8-12 week roadmap.',
    primaryCTA: { text: 'Start Specialization Fit Test', link: '/fit-test?stage=firstyear' },
    heroImage: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644'
  },
  stepper: ['Fit Test', 'Top Matches', 'Reality Check', 'Skills Map', 'Roadmap', 'Projects'],
  specializationGrid: {
    engineering: [
      { name: 'Web Development', roles: ['Frontend', 'Backend', 'Fullstack'], link: '/tracks/web', aiRisk: 'Medium' },
      { name: 'Data Science', roles: ['Analyst', 'Engineer', 'ML Engineer'], link: '/tracks/data', aiRisk: 'Low' },
      { name: 'AI / ML', roles: ['ML Engineer', 'AI Researcher'], link: '/tracks/ai', aiRisk: 'Low' },
      { name: 'Cybersecurity', roles: ['Security Analyst', 'Pentester'], link: '/tracks/cyber', aiRisk: 'Low' },
      { name: 'Cloud / DevOps', roles: ['DevOps Engineer', 'Cloud Architect'], link: '/tracks/cloud', aiRisk: 'Low' },
      { name: 'QA / Testing', roles: ['QA Engineer', 'Automation Tester'], link: '/tracks/qa', aiRisk: 'High' },
      { name: 'UI/UX Design', roles: ['UI Designer', 'UX Researcher'], link: '/tracks/uiux', aiRisk: 'Medium' }
    ],
    management: [
      { name: 'Marketing', roles: ['Digital Marketer', 'Brand Manager'], link: '/tracks/marketing', aiRisk: 'Medium' },
      { name: 'Finance', roles: ['Financial Analyst', 'Investment Banker'], link: '/tracks/finance', aiRisk: 'Low' },
      { name: 'HR', roles: ['HR Manager', 'Recruiter'], link: '/tracks/hr', aiRisk: 'Medium' },
      { name: 'Operations', roles: ['Operations Manager', 'Supply Chain'], link: '/tracks/ops', aiRisk: 'Low' },
      { name: 'Analytics', roles: ['Business Analyst', 'Data Analyst'], link: '/tracks/analytics', aiRisk: 'Medium' },
      { name: 'Product Management', roles: ['Product Manager', 'Product Owner'], link: '/tracks/product', aiRisk: 'Low' }
    ]
  },
  roadmapPreview: [
    { weeks: 'Weeks 1-2', focus: 'Basics and Fundamentals' },
    { weeks: 'Weeks 3-6', focus: 'Core Concepts and Tools' },
    { weeks: 'Weeks 7-10', focus: 'Real Projects' },
    { weeks: 'Weeks 11-12', focus: 'Portfolio + Interviews' }
  ],
  projectsPreview: [
    { title: 'E-commerce Website', tech: 'React, Node.js, MongoDB', locked: false },
    { title: 'Task Management App', tech: 'React, Firebase', locked: false },
    { title: 'Portfolio Website', tech: 'HTML, CSS, JavaScript', locked: true }
  ],
  aiRiskExplainer: 'AI Risk shows how much of this specialization AI might automate. Low = Future-safe. High = Skills need constant updating.'
};

export const switchData = {
  hero: {
    breadcrumb: ['Home', 'Pathways', 'Final Year / Switch'],
    title: 'Final Year / Switch: Skills > Degree',
    subtitle: 'Skill audit -> role match -> bridge plan -> portfolio.',
    primaryCTA: { text: 'Start Skill Audit', link: '/skill-audit' },
    heroImage: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f'
  },
  stepper: ['Skill Audit', 'Role Match', 'Skill Gaps', 'Bridge Roadmap', 'Portfolio', 'Interview Prep'],
  skillAuditIntake: {
    title: 'Tell Us About Your Current Skills',
    fields: [
      'Programming languages you know',
      'Tools/frameworks you have used',
      'Projects you have built',
      'Time available per week',
      'Target salary (optional)'
    ]
  },
  roleMatchCards: [
    { role: 'Frontend Developer', entry: 'HTML, CSS, JavaScript', time: '8-12 weeks', aiRisk: 'Medium', salary: 'Rs 3-6 LPA' },
    { role: 'Data Analyst', entry: 'Excel, SQL, Python basics', time: '10-14 weeks', aiRisk: 'Medium', salary: 'Rs 4-7 LPA' },
    { role: 'Backend Developer', entry: 'Node.js / Python / Java', time: '10-16 weeks', aiRisk: 'Low', salary: 'Rs 4-8 LPA' },
    { role: 'QA Engineer', entry: 'Testing basics, automation', time: '6-10 weeks', aiRisk: 'High', salary: 'Rs 3-5 LPA' }
  ],
  gapReport: {
    youHave: ['HTML, CSS basics', 'Python fundamentals', 'Git basics'],
    youNeed: ['React.js', 'State management', 'API integration', 'Real projects']
  },
  bridgeRoadmap: {
    duration: '10-12 weeks',
    milestones: [
      'Week 1-2: React fundamentals',
      'Week 3-5: State and hooks',
      'Week 6-8: API and authentication',
      'Week 9-10: Portfolio project',
      'Week 11-12: Interview prep'
    ]
  },
  portfolioBuilder: {
    proofProjects: [
      'E-commerce with cart and checkout',
      'Social media dashboard',
      'Weather app with API'
    ],
    resumeBullets: [
      'Built responsive e-commerce site with React',
      'Integrated payment gateway and authentication',
      'Deployed on Vercel with CI/CD'
    ]
  },
  interviewKit: {
    mockQuestions: ['Explain React hooks', 'What is REST API?', 'How do you handle state?'],
    checklist: ['Resume ready', 'GitHub profile', 'Portfolio live', 'Mock interview']
  }
};
